import { useEffect, useState } from 'react';
import { costCentersApi, pettyCashApi, taxApi } from '../../api/services';
import type {
  PettyCashFundDto, PettyCashInvoiceDto, PettyCashSettlementDto, PettyCashSettlementLineDto, SavePettyCashFundDto, SavePettyCashInvoiceDto,
} from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Stat, Table, Tabs, fmtDate, fmtMoney, fmtNumber, money, round2, todayIso, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';
import { PERM, useCan } from '../../ui/access';
import { AttachmentViewer, ScanButton, ScanSummary, type ScanOutcome } from '../../ui/invoiceScan';

type TabKey = 'invoices' | 'settlements' | 'funds';
const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  P: { label: 'Pendiente', tone: 'warn' }, L: { label: 'Liquidada', tone: 'ok' }, N: { label: 'Anulada', tone: 'bad' },
};

/** Caja chica con fondo fijo: facturas pequeñas, liquidación con asiento y fondos. La reposición del efectivo se hace en Bancos. */
export function PettyCashPage() {
  const [tab, setTab] = useState<TabKey>('invoices');
  const funds = useAsync(() => pettyCashApi.funds(), []);
  const [fundId, setFundId] = useState<number | ''>('');
  useEffect(() => {
    const list = funds.data ?? [];
    if (fundId === '' && list.length > 0) setFundId((list.find((f) => f.status === 'A') ?? list[0]).id);
  }, [funds.data, fundId]);
  const fund = (funds.data ?? []).find((f) => f.id === fundId) ?? null;
  return (
    <>
      <PageHeader title="Caja chica" subtitle="Facturas pequeñas pagadas en efectivo y su liquidación contable" />
      <PageHelp page="pettyCash" />
      {(funds.data ?? []).length > 0 && (
        <div className="acc-toolbar">
          <Field label="Fondo"><Select value={fundId} onChange={(v) => setFundId(v)} options={(funds.data ?? []).map((f) => ({ value: f.id, label: f.status === 'A' ? f.name : `${f.name} (inactivo)` }))} /></Field>
        </div>
      )}
      {fund && (
        <div className="acc-stats">
          <Stat label="Monto del fondo" value={fmtMoney(fund.fundAmount)} />
          <Stat label={`Pendiente de liquidar (${fund.pendingCount})`} value={fmtMoney(fund.pendingTotal)} />
          <Stat label="Efectivo disponible" value={fmtMoney(fund.available)} tone={fund.available <= fund.fundAmount * 0.2 ? 'neg' : undefined} />
          <Stat label="Saldo contable de la cuenta" value={fmtMoney(fund.accountBalance)} />
        </div>
      )}
      {fund && Math.abs(fund.accountBalance - fund.fundAmount) > 0.004 && fund.pendingCount === 0 && (
        <Alert kind="warn">El saldo contable de la caja chica ({fmtMoney(fund.accountBalance)}) no coincide con el monto del fondo ({fmtMoney(fund.fundAmount)}). Si acaba de liquidar, registre la reposición en Bancos (débito caja chica, crédito banco).</Alert>
      )}
      <Tabs<TabKey> tabs={[{ key: 'invoices', label: 'Facturas' }, { key: 'settlements', label: 'Liquidaciones' }, { key: 'funds', label: 'Fondos' }]} active={tab} onChange={setTab} />
      {tab === 'invoices' && (fund ? <InvoicesTab fund={fund} onChanged={funds.reload} /> : <NoFund state={funds} goFunds={() => setTab('funds')} />)}
      {tab === 'settlements' && (fund ? <SettlementsTab fund={fund} onChanged={funds.reload} /> : <NoFund state={funds} goFunds={() => setTab('funds')} />)}
      {tab === 'funds' && <FundsTab state={funds} onSaved={(id) => { setFundId(id); funds.reload(); }} />}
    </>
  );
}

function NoFund({ state, goFunds }: { state: { loading: boolean }; goFunds: () => void }) {
  if (state.loading) return null;
  return <Alert kind="info">Primero cree un fondo de caja chica (con su cuenta contable). <Button kind="link" onClick={goFunds}>Ir a Fondos</Button></Alert>;
}

// ================================================================== Facturas

function InvoicesTab({ fund, onChanged }: { fund: PettyCashFundDto; onChanged: () => void }) {
  const [status, setStatus] = useState<'' | 'P' | 'L' | 'N'>('P');
  const list = useAsync(() => pettyCashApi.invoices({ fundId: fund.id, status: status || undefined }), [fund.id, status]);
  const canOperate = useCan(PERM.cashOperate);
  const [editing, setEditing] = useState<PettyCashInvoiceDto | 'new' | null>(null);
  const [settling, setSettling] = useState(false);
  const [photo, setPhoto] = useState<PettyCashInvoiceDto | null>(null);
  const { run, busy } = useAction();
  const reload = () => { list.reload(); onChanged(); };
  return (
    <>
      <div className="acc-toolbar">
        <Field label="Estado"><Select value={status} onChange={(v) => setStatus(v)} placeholder="Todas" options={[{ value: 'P', label: 'Pendientes' }, { value: 'L', label: 'Liquidadas' }, { value: 'N', label: 'Anuladas' }]} /></Field>
        {canOperate && <Button kind="primary" disabled={fund.status !== 'A'} onClick={() => setEditing('new')}>Nueva factura</Button>}
        {canOperate && <Button disabled={fund.pendingCount === 0} onClick={() => setSettling(true)}>Liquidar…</Button>}
        <Button busy={busy} onClick={() => run(() => pettyCashApi.exportInvoices({ fundId: fund.id, status: status || undefined }))}>Exportar CSV</Button>
      </div>
      {fund.status !== 'A' && <Alert kind="info">El fondo está inactivo: no admite facturas nuevas, pero puede liquidar las pendientes.</Alert>}
      <Loadable state={list}>
        {(rows) => (
          <Table<PettyCashInvoiceDto> rows={rows} rowKey={(i) => i.id} empty={status === 'P' ? 'No hay facturas pendientes de liquidar.' : 'No hay facturas.'} columns={[
            { key: 'f', title: 'Fecha', render: (i) => fmtDate(i.date) },
            { key: 'p', title: 'Proveedor', render: (i) => <>{i.supplier}<div className="acc-muted">{[i.supplierId, i.invoiceNumber ? `Factura ${i.invoiceNumber}` : null].filter(Boolean).join(' · ')}</div></> },
            { key: 'd', title: 'Descripción', render: (i) => <>{i.description}<div className="acc-muted">{i.expenseAccount} · {i.expenseAccountName}{i.costCenterName ? ` · ${i.costCenterName}` : ''}</div></> },
            money<PettyCashInvoiceDto>('s', 'Subtotal', (i) => i.subtotal),
            { key: 'i', title: 'Impuesto', align: 'right', render: (i) => (i.tax > 0 ? <>{fmtMoney(i.tax)}<div className="acc-muted">{fmtNumber(i.taxPercent ?? 0)} %</div></> : <span className="acc-muted">Exento</span>) },
            money<PettyCashInvoiceDto>('t', 'Total', (i) => i.total),
            { key: 'e', title: 'Estado', render: (i) => (
              <>
                <Badge tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Badge>
                {i.settlementEntryCode && <div><Link to={`/entries/${i.settlementEntryCode}`}>Asiento {i.settlementEntryCode}</Link></div>}
                {i.cancellationReason && <div className="acc-muted">{i.cancellationReason}</div>}
              </>
            ) },
            { key: 'x', title: '', render: (i) => (
              <>
                {i.hasAttachment && <Button kind="link" onClick={() => setPhoto(i)}>Ver factura</Button>}
                {canOperate && i.status === 'P' && <Button kind="link" onClick={() => setEditing(i)}>Editar…</Button>}
                {canOperate && i.status === 'P' && <Button kind="link" busy={busy} onClick={() => run(async (reason) => { await pettyCashApi.cancelInvoice(i.id, reason ?? ''); reload(); },
                  { confirm: `Anular la factura de ${i.supplier} por ${fmtMoney(i.total)}? No tiene asiento: sólo sale del fondo.`, danger: true, requireReason: true, success: 'Factura anulada.' })}>Anular…</Button>}
              </>
            ) },
          ]} />
        )}
      </Loadable>
      {editing && <InvoiceModal fund={fund} invoice={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {settling && <SettleModal fund={fund} onClose={() => setSettling(false)} onDone={() => { setSettling(false); reload(); }} />}
      {photo && <AttachmentViewer origin="pettyCash" reference={photo.id} title={`Factura de ${photo.supplier}${photo.invoiceNumber ? ` · ${photo.invoiceNumber}` : ''}`} onClose={() => setPhoto(null)} />}
    </>
  );
}

function InvoiceModal({ fund, invoice, onClose, onSaved }: { fund: PettyCashFundDto; invoice: PettyCashInvoiceDto | null; onClose: () => void; onSaved: () => void }) {
  const rates = useAsync(() => taxApi.rates().catch(() => []), []);
  const centers = useAsync(() => costCentersApi.list().catch(() => []), []);
  const [f, setF] = useState<SavePettyCashInvoiceDto>({
    fundId: fund.id, date: invoice?.date.slice(0, 10) ?? todayIso(), supplier: invoice?.supplier ?? '', supplierId: invoice?.supplierId ?? '',
    invoiceNumber: invoice?.invoiceNumber ?? '', description: invoice?.description ?? '', expenseAccount: invoice?.expenseAccount ?? '',
    costCenterId: invoice?.costCenterId ?? null, subtotal: invoice?.subtotal ?? 0, taxPercent: invoice?.taxPercent ?? 0, tax: invoice?.tax ?? 0,
  });
  const percentOptions = Array.from(new Set([0, ...(rates.data ?? []).filter((r) => r.active).map((r) => r.percent), ...(f.taxPercent ? [f.taxPercent] : [])]))
    .sort((a, b) => a - b).map((p) => ({ value: p, label: p === 0 ? 'Exento' : `${fmtNumber(p, 4)} %` }));
  const setAmounts = (subtotal: number, taxPercent: number) => setF((x) => ({ ...x, subtotal, taxPercent, tax: round2(subtotal * taxPercent / 100) }));
  const total = round2(f.subtotal + f.tax);
  const { run, busy, fieldErrors } = useAction();
  const [scan, setScan] = useState<ScanOutcome | null>(null);
  /** Precarga lo que leyó la IA. El total de caja chica es subtotal + impuesto: otros cargos quedan dentro del subtotal. */
  const applyScan = (o: ScanOutcome) => {
    const r = o.result;
    const tax = round2(r.taxAmount ?? 0);
    const subtotal = r.total != null ? round2(r.total - tax) : (r.subtotal ?? f.subtotal);
    setF((x) => ({
      ...x,
      date: r.invoiceDate ? r.invoiceDate.slice(0, 10) : x.date,
      supplier: r.supplierName?.slice(0, 150) ?? x.supplier,
      supplierId: r.supplierTaxId?.slice(0, 50) ?? x.supplierId,
      invoiceNumber: r.invoiceNumber?.slice(0, 50) ?? x.invoiceNumber,
      description: r.description?.slice(0, 300) ?? x.description,
      expenseAccount: r.suggestedExpenseAccount ?? x.expenseAccount,
      costCenterId: r.suggestedExpenseAccount ? (r.suggestedCostCenterId ?? null) : x.costCenterId,
      subtotal, tax,
      taxPercent: tax > 0 ? (r.taxRatePercent ?? x.taxPercent ?? 0) : 0,
      attachmentId: r.attachmentId,
    }));
    setScan(o);
  };
  return (
    <Modal title={invoice ? 'Editar factura de caja chica' : `Nueva factura · ${fund.name}`} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto = { ...f, supplierId: f.supplierId || null, invoiceNumber: f.invoiceNumber || null, taxPercent: f.taxPercent || null };
        if (invoice) await pettyCashApi.updateInvoice(invoice.id, dto); else await pettyCashApi.createInvoice(dto);
        onSaved();
      }, { success: invoice ? 'Factura actualizada.' : 'Factura registrada.' })}>
        {!scan && <div className="acc-toolbar"><ScanButton origin="pettyCash" fundId={fund.id} onResult={applyScan} label={invoice ? 'Leer otra foto' : 'Leer factura con foto'} /></div>}
        {scan && <ScanSummary outcome={scan} onDiscard={() => { setScan(null); setF({ ...f, attachmentId: null }); }} />}
        {scan && (scan.result.otherCharges ?? 0) > 0 && <Alert kind="info">La factura trae otros cargos por {fmtMoney(scan.result.otherCharges)}: quedaron sumados al subtotal para que el total coincida.</Alert>}
        {scan && f.tax > 0 && !f.taxPercent && <Alert kind="warn">La IA no pudo leer la tarifa del impuesto: elíjala.</Alert>}
        {scan && !scan.result.suggestedExpenseAccount && <Alert kind="info">Es la primera factura de este proveedor en caja chica: elija la cuenta de gasto. La próxima vez se sugerirá la misma.</Alert>}
        <div className="acc-grid-form">
          <Field label="Fecha"><Input type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} required /></Field>
          <Field label="Proveedor" error={fieldErrors.Supplier}><Input value={f.supplier} onChange={(v) => setF({ ...f, supplier: v })} maxLength={150} required /></Field>
          <Field label="RUC / identificación"><Input value={f.supplierId ?? ''} onChange={(v) => setF({ ...f, supplierId: v })} maxLength={50} /></Field>
          <Field label="Número de factura"><Input value={f.invoiceNumber ?? ''} onChange={(v) => setF({ ...f, invoiceNumber: v })} maxLength={50} /></Field>
          <Field label="Descripción" wide error={fieldErrors.Description}><Input value={f.description} onChange={(v) => setF({ ...f, description: v })} maxLength={300} required /></Field>
          <Field label="Cuenta de gasto" error={fieldErrors.ExpenseAccount}><AccountPicker value={f.expenseAccount} onChange={(v) => setF({ ...f, expenseAccount: v })} /></Field>
          {(centers.data ?? []).length > 0 && (
            <Field label="Centro de costo"><Select value={f.costCenterId ?? ''} onChange={(v) => setF({ ...f, costCenterId: v || null })} placeholder="Sin centro" options={(centers.data ?? []).filter((c) => c.status === 'A').map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))} /></Field>
          )}
          <Field label="Subtotal (sin impuesto)" error={fieldErrors.Subtotal}><NumberInput value={f.subtotal} min={0} onChange={(v) => setAmounts(v, f.taxPercent ?? 0)} required /></Field>
          <Field label="Tarifa del impuesto"><Select value={f.taxPercent ?? 0} onChange={(v) => setAmounts(f.subtotal, v)} options={percentOptions} /></Field>
          <Field label="Impuesto" hint="Se calcula solo; ajústelo si la factura trae otro redondeo" error={fieldErrors.Tax}><NumberInput value={f.tax} min={0} onChange={(v) => setF({ ...f, tax: v })} disabled={!f.taxPercent} /></Field>
        </div>
        <p>Total de la factura: <strong>{fmtMoney(total)}</strong>{fund.invoiceLimit ? <span className="acc-muted"> · máximo por factura {fmtMoney(fund.invoiceLimit)}</span> : null}</p>
        {!fund.taxAccount && f.tax > 0 && <Alert kind="info">Este fondo no tiene cuenta de impuesto: al liquidar, el impuesto se suma al gasto.</Alert>}
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!f.expenseAccount || f.subtotal <= 0}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================== Liquidación

function SettleModal({ fund, onClose, onDone }: { fund: PettyCashFundDto; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(todayIso());
  const preview = useAsync(() => pettyCashApi.previewSettlement({ fundId: fund.id, date }), [fund.id, date]);
  const { run, busy } = useAction();
  return (
    <Modal title={`Liquidar caja chica · ${fund.name}`} onClose={onClose} size="lg">
      <div className="acc-toolbar">
        <Field label="Fecha del asiento" hint="Incluye las facturas pendientes hasta esta fecha"><Input type="date" value={date} onChange={setDate} /></Field>
      </div>
      <Loadable state={preview}>
        {(p) => (
          <>
            {p.blockers.map((b) => <Alert key={b} kind="error">{b}</Alert>)}
            {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
            {p.invoiceCount > 0 && (
              <>
                <div className="acc-stats">
                  <Stat label="Facturas" value={String(p.invoiceCount)} />
                  <Stat label="Subtotal" value={fmtMoney(p.subtotal)} />
                  <Stat label="Impuesto" value={fmtMoney(p.tax)} />
                  <Stat label="Total" value={fmtMoney(p.total)} />
                </div>
                <Card title="Asiento que se va a registrar">
                  <Table<PettyCashSettlementLineDto> rows={p.lines} rowKey={(l) => `${l.accountNumber}-${l.costCenterId ?? 0}-${l.debitCredit}`} columns={[
                    { key: 'c', title: 'Cuenta', render: (l) => <>{l.accountNumber} · {l.accountName}{l.costCenterName ? <div className="acc-muted">{l.costCenterName}</div> : null}</> },
                    { key: 'd', title: 'Débito', align: 'right', render: (l) => (l.debitCredit === 'D' ? fmtMoney(l.amount) : '') },
                    { key: 'k', title: 'Crédito', align: 'right', render: (l) => (l.debitCredit === 'C' ? fmtMoney(l.amount) : '') },
                  ]} />
                </Card>
                <Alert kind="info">Después de liquidar, reponga el efectivo en Bancos con un retiro o transferencia: débito a la caja chica y crédito al banco, por {fmtMoney(p.total)}.</Alert>
              </>
            )}
            <div className="acc-actions">
              <Button onClick={onClose}>Cancelar</Button>
              <Button kind="primary" disabled={!p.canPost} busy={busy} onClick={() => run(async () => { await pettyCashApi.settle({ fundId: fund.id, date }); onDone(); },
                { confirm: `Registrar la liquidación de ${p.invoiceCount} factura(s) por ${fmtMoney(p.total)}?`, success: 'Liquidación registrada.' })}>Registrar liquidación</Button>
            </div>
          </>
        )}
      </Loadable>
    </Modal>
  );
}

function SettlementsTab({ fund, onChanged }: { fund: PettyCashFundDto; onChanged: () => void }) {
  const list = useAsync(() => pettyCashApi.settlements(fund.id), [fund.id]);
  const canOperate = useCan(PERM.cashOperate);
  const [viewing, setViewing] = useState<PettyCashSettlementDto | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Loadable state={list}>
        {(rows) => (
          <Table<PettyCashSettlementDto> rows={rows} rowKey={(s) => s.id} empty="Todavía no hay liquidaciones de este fondo." columns={[
            { key: 'f', title: 'Fecha', render: (s) => fmtDate(s.date) },
            { key: 'n', title: 'Facturas', align: 'right', render: (s) => <Button kind="link" onClick={() => setViewing(s)}>{s.invoiceCount}</Button> },
            money<PettyCashSettlementDto>('s', 'Subtotal', (s) => s.subtotal),
            money<PettyCashSettlementDto>('i', 'Impuesto', (s) => s.tax),
            money<PettyCashSettlementDto>('t', 'Total', (s) => s.total),
            { key: 'a', title: 'Asiento', render: (s) => <><Link to={`/entries/${s.entryCode}`}>{s.entryCode}</Link>{s.cancellationEntryCode && <div className="acc-muted">Reverso <Link to={`/entries/${s.cancellationEntryCode}`}>{s.cancellationEntryCode}</Link></div>}</> },
            { key: 'e', title: 'Estado', render: (s) => (s.status === 'A' ? <Badge tone="ok">Vigente</Badge> : <><Badge tone="bad">Anulada</Badge>{s.cancellationReason && <div className="acc-muted">{s.cancellationReason}</div>}</>) },
            { key: 'x', title: '', render: (s) => (canOperate && s.status === 'A' ? (
              <Button kind="link" busy={busy} onClick={() => run(async (reason) => { await pettyCashApi.cancelSettlement(s.id, reason ?? ''); list.reload(); onChanged(); },
                { confirm: `Anular la liquidación del ${fmtDate(s.date)}? Se registra el reverso del asiento y sus ${s.invoiceCount} factura(s) vuelven a quedar pendientes. Si ya repuso el efectivo en Bancos, ese movimiento no se toca.`, danger: true, requireReason: true, success: 'Liquidación anulada.' })}>Anular…</Button>
            ) : null) },
          ]} />
        )}
      </Loadable>
      {viewing && <SettlementInvoicesModal settlement={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

function SettlementInvoicesModal({ settlement, onClose }: { settlement: PettyCashSettlementDto; onClose: () => void }) {
  const list = useAsync(() => pettyCashApi.invoices({ settlementId: settlement.id }), [settlement.id]);
  return (
    <Modal title={`Facturas de la liquidación del ${fmtDate(settlement.date)}`} onClose={onClose} size="lg">
      {settlement.status === 'N' && <Alert kind="info">La liquidación está anulada: sus facturas volvieron a quedar pendientes.</Alert>}
      <Loadable state={list}>
        {(rows) => (
          <Table<PettyCashInvoiceDto> rows={rows} rowKey={(i) => i.id} empty="Sin facturas." columns={[
            { key: 'f', title: 'Fecha', render: (i) => fmtDate(i.date) },
            { key: 'p', title: 'Proveedor', render: (i) => i.supplier },
            { key: 'd', title: 'Descripción', render: (i) => i.description },
            { key: 'c', title: 'Cuenta', render: (i) => `${i.expenseAccount} · ${i.expenseAccountName ?? ''}` },
            money<PettyCashInvoiceDto>('t', 'Total', (i) => i.total),
          ]} />
        )}
      </Loadable>
      <div className="acc-actions"><Button onClick={onClose}>Cerrar</Button></div>
    </Modal>
  );
}

// ================================================================== Fondos

function FundsTab({ state, onSaved }: { state: ReturnType<typeof useAsync<PettyCashFundDto[]>>; onSaved: (id: number) => void }) {
  const canSetup = useCan(PERM.setup);
  const [editing, setEditing] = useState<PettyCashFundDto | 'new' | null>(null);
  return (
    <>
      {canSetup && <div className="acc-toolbar"><Button kind="primary" onClick={() => setEditing('new')}>Nuevo fondo</Button></div>}
      <Loadable state={state}>
        {(rows) => (
          <Table<PettyCashFundDto> rows={rows} rowKey={(f) => f.id} empty="Todavía no hay fondos de caja chica." columns={[
            { key: 'n', title: 'Fondo', render: (f) => <>{f.name}{f.responsible && <div className="acc-muted">Responsable: {f.responsible}</div>}</> },
            { key: 'c', title: 'Cuenta', render: (f) => `${f.account} · ${f.accountName ?? ''}` },
            { key: 'i', title: 'Impuesto', render: (f) => (f.taxAccount ? `${f.taxAccount} · ${f.taxAccountName ?? ''}` : <span className="acc-muted">Se suma al gasto</span>) },
            money<PettyCashFundDto>('m', 'Monto del fondo', (f) => f.fundAmount),
            { key: 'l', title: 'Máximo por factura', align: 'right', render: (f) => (f.invoiceLimit ? fmtMoney(f.invoiceLimit) : <span className="acc-muted">Sin límite</span>) },
            { key: 'e', title: 'Estado', render: (f) => <Badge tone={f.status === 'A' ? 'ok' : 'neutral'}>{f.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
            { key: 'x', title: '', render: (f) => (canSetup ? <Button kind="link" onClick={() => setEditing(f)}>Editar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {editing && <FundModal fund={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={(id) => { setEditing(null); onSaved(id); }} />}
    </>
  );
}

function FundModal({ fund, onClose, onSaved }: { fund: PettyCashFundDto | null; onClose: () => void; onSaved: (id: number) => void }) {
  const [f, setF] = useState<SavePettyCashFundDto>({
    name: fund?.name ?? '', responsible: fund?.responsible ?? '', account: fund?.account ?? '', taxAccount: fund?.taxAccount ?? '',
    fundAmount: fund?.fundAmount ?? 0, invoiceLimit: fund?.invoiceLimit ?? null, status: fund?.status ?? 'A',
  });
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title={fund ? `Editar fondo ${fund.name}` : 'Nuevo fondo de caja chica'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto = { ...f, responsible: f.responsible || null, taxAccount: f.taxAccount || null, invoiceLimit: f.invoiceLimit || null };
        const saved = fund ? await pettyCashApi.updateFund(fund.id, dto) : await pettyCashApi.createFund(dto);
        onSaved(saved.id);
      }, { success: 'Fondo guardado.' })}>
        {fund && fund.pendingCount > 0 && <Alert kind="info">El fondo tiene facturas pendientes: sus cuentas no se pueden cambiar hasta liquidarlas.</Alert>}
        <div className="acc-grid-form">
          <Field label="Nombre" error={fieldErrors.Name}><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} maxLength={100} required /></Field>
          <Field label="Responsable"><Input value={f.responsible ?? ''} onChange={(v) => setF({ ...f, responsible: v })} maxLength={100} /></Field>
          <Field label="Cuenta de la caja chica" hint="Cuenta de activo de detalle, por ejemplo Caja chica"><AccountPicker value={f.account} onChange={(v) => setF({ ...f, account: v })} /></Field>
          <Field label="Cuenta del impuesto de compras" hint="Crédito fiscal (opcional). Vacía = el impuesto se suma al gasto"><AccountPicker value={f.taxAccount ?? ''} onChange={(v) => setF({ ...f, taxAccount: v })} /></Field>
          <Field label="Monto del fondo" error={fieldErrors.FundAmount}><NumberInput value={f.fundAmount} min={0} onChange={(v) => setF({ ...f, fundAmount: v })} required /></Field>
          <Field label="Máximo por factura" hint="Vacío = sin límite"><NumberInput value={f.invoiceLimit} min={0} onChange={(v) => setF({ ...f, invoiceLimit: v || null })} /></Field>
          <Field label="Estado"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        </div>
        <Alert kind="info">Crear el fondo no genera asiento. Entregue el efectivo con un retiro en Bancos: débito a la cuenta de la caja chica y crédito al banco, por el monto del fondo.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!f.account || f.fundAmount <= 0}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
